import React, { Fragment, Component } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { connect, ConnectedProps } from 'react-redux';
import { Form } from 'react-final-form';
import {
    crudCreate,
    DateInput,
    ReferenceInput,
    SelectInput,
    useTranslate,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import ActionAdd from '@material-ui/icons/PlaylistAdd';
import ActionCheck from '@material-ui/icons/CheckCircle';
import AlertError from '@material-ui/icons/ErrorOutline';
import classnames from 'classnames';
import {
    RequestType,
    OrderStatus,
    PickupTimeFrom,
    PickupTimeTo,
} from '../../constants';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
} from '@material-ui/core';
import feathersClient from '../../rest/feathersClient';

const sanitizeRestProps: (any) => any = ({
    basePath,
    classes,
    crudCreate,
    filterValues,
    label,
    resource,
    selectedIds,
    cityId,
    countryId,
    userId,
    ...rest
}) => rest;

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    input: {
        display: 'block',
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 200,
    },
    menu: {
        width: 200,
    },
    actionButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: fade(theme.palette.primary.main, 0.12),
            // Reset on mouse devices
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
    },
    confirmPrimary: {
        color: theme.palette.primary.main,
    },
    confirmWarning: {
        color: theme.palette.error.main,
        '&:hover': {
            backgroundColor: fade(theme.palette.error.main, 0.12),
            // Reset on mouse devices
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
    },
    iconPaddingStyle: {
        paddingRight: '0.5em',
    },
}));

type Styles = {
    input: string;
    iconPaddingStyle: string;
    confirmPrimary: string;
    confirmWarning: string;
    actionButton: string;
};

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
    classes: Styles;
    cancel: string;
    label: string;
    resource: string;
    selectedIds: any[];
    icon: object;
    confirm: string;
    confirmColor: string;
    translate: (a: string, b: object) => string;
    type: string;
    date: string;
    from: number;
    to: number;
    cityId: string;
    countryId: number;
    userId: number;
};

type State = {
    isOpen: boolean;
    loading: boolean;
    previewLoading: boolean;
    expandedSelectedIds: number[];
    autoAddedItems: any[];
    previewItems: any[];
    previewError: string | null;
};

class CreateRequest extends Component<Props, State> {
    state: State = {
        isOpen: false,
        loading: false,
        previewLoading: false,
        expandedSelectedIds: [],
        autoAddedItems: [],
        previewItems: [],
        previewError: null,
    };

    normalizeIds = (ids: any[]) =>
        (ids || []).map(id => Number(id)).filter(id => !Number.isNaN(id));

    loadExpandedPreview = async () => {
        const { selectedIds } = this.props;
        this.setState({ previewLoading: true, previewError: null });
        try {
            const {
                expandedIds,
                autoAddedItems,
                previewItems,
            } = await this.expandStorageIdsWithParts(selectedIds || []);
            this.setState({
                expandedSelectedIds: expandedIds,
                autoAddedItems,
                previewItems,
                previewLoading: false,
            });
        } catch (error) {
            this.setState({
                previewLoading: false,
                autoAddedItems: [],
                previewItems: [],
                previewError: 'Unable to preview parts/parents expansion.',
            });
        }
    };

    handleClick = e => {
        this.setState({ isOpen: true }, this.loadExpandedPreview);
        e.stopPropagation();
    };

    handleDialogClose = () => {
        this.setState({
            isOpen: false,
            previewLoading: false,
            previewError: null,
            expandedSelectedIds: [],
            autoAddedItems: [],
            previewItems: [],
        });
    };

    expandStorageIdsWithParts = async (selectedIds: any[]) => {
        const toNumberId = (id: any) => Number(id);
        const normalizedSelectedIds = (selectedIds || [])
            .map(toNumberId)
            .filter(id => !Number.isNaN(id));

        if (!normalizedSelectedIds.length) {
            return { expandedIds: [], autoAddedItems: [], previewItems: [] };
        }

        const storagesService = feathersClient.service('storages');
        const selectedResponse = await storagesService.find({
            query: {
                id: { $in: normalizedSelectedIds },
                $limit: normalizedSelectedIds.length,
            },
        });
        const selectedStorages = selectedResponse?.data || [];
        const storageMap = new Map<number, any>();
        selectedStorages.forEach(s => {
            const id = Number(s?.id);
            if (!Number.isNaN(id)) storageMap.set(id, s);
        });

        const parentIds = new Set<number>();
        const finalIds = new Set<number>(normalizedSelectedIds);
        const autoAddedMap = new Map<number, string>();

        selectedStorages.forEach(storage => {
            if (storage?.isPart && storage?.parentId) {
                const parentId = Number(storage.parentId);
                if (!Number.isNaN(parentId)) {
                    parentIds.add(parentId);
                    if (!finalIds.has(parentId)) {
                        finalIds.add(parentId);
                        autoAddedMap.set(parentId, 'Parent of selected part');
                    }
                }
            } else if (storage?.id) {
                parentIds.add(Number(storage.id));
            }
        });

        const parentIdsArray = Array.from(parentIds).filter(
            id => !Number.isNaN(id)
        );

        if (parentIdsArray.length) {
            const siblingPartsResponse = await storagesService.find({
                query: {
                    isPart: true,
                    parentId: { $in: parentIdsArray },
                    $limit: 1000,
                },
            });
            const siblingParts = siblingPartsResponse?.data || [];
            siblingParts.forEach(part => {
                const partId = Number(part?.id);
                if (!Number.isNaN(partId)) {
                    storageMap.set(partId, part);
                    if (!finalIds.has(partId)) {
                        finalIds.add(partId);
                        autoAddedMap.set(
                            partId,
                            'Sibling part in same parent group'
                        );
                    }
                }
            });
        }

        const missingAutoAddedParentIds = Array.from(
            autoAddedMap.keys()
        ).filter(id => !storageMap.has(id));
        if (missingAutoAddedParentIds.length) {
            const parentsResponse = await storagesService.find({
                query: {
                    id: { $in: missingAutoAddedParentIds },
                    $limit: missingAutoAddedParentIds.length,
                },
            });
            (parentsResponse?.data || []).forEach(parent => {
                const parentId = Number(parent?.id);
                if (!Number.isNaN(parentId)) storageMap.set(parentId, parent);
            });
        }

        const autoAddedItems = Array.from(autoAddedMap.entries()).map(
            ([id, reason]) => {
                const storage = storageMap.get(id);
                return {
                    id,
                    code: storage?.code || `#${id}`,
                    isPart: !!storage?.isPart,
                    reason,
                };
            }
        );

        const previewItems = Array.from(finalIds)
            .map(id => {
                const storage = storageMap.get(id);
                const isSelected = normalizedSelectedIds.includes(id);
                const reason = isSelected
                    ? 'Selected manually'
                    : autoAddedMap.get(id) || 'Auto included';
                return {
                    id,
                    code: storage?.code || `#${id}`,
                    isPart: !!storage?.isPart,
                    reason,
                    isSelected,
                };
            })
            .sort((a, b) => Number(b.isSelected) - Number(a.isSelected));

        return {
            expandedIds: Array.from(finalIds),
            autoAddedItems,
            previewItems,
        };
    };

    createRequest = async values => {
        const { crudCreate, selectedIds, countryId, userId } = this.props;
        const { type, date, from, to, cityId, partnerId, orderId } = values;

        if (
            partnerId &&
            orderId &&
            type &&
            date &&
            from &&
            to &&
            cityId &&
            countryId &&
            userId &&
            selectedIds &&
            selectedIds.length
        ) {
            const expandedStorageIds =
                this.state.expandedSelectedIds.length > 0
                    ? this.state.expandedSelectedIds
                    : (await this.expandStorageIdsWithParts(selectedIds))
                          .expandedIds;
            const data = {
                partnerId,
                orderId,
                type,
                date,
                from,
                to,
                cityId,
                countryId,
                userId,
                request_lines: expandedStorageIds.map(id => ({
                    storageId: id,
                })),
            };

            this.setState({ loading: true });
            crudCreate('requests', data, '/requests', 'show');
        }
    };

    render() {
        const {
            classes,
            translate,
            label = 'Create Request',
            cancel = 'ra.action.cancel',
            confirm = 'ra.action.confirm',
            confirmColor = 'primary',
            icon = <ActionAdd />,
            resource,
            selectedIds,
            countryId,
            userId,
            ...rest
        } = this.props;
        const {
            isOpen,
            loading,
            previewLoading,
            expandedSelectedIds,
            autoAddedItems,
            previewItems,
            previewError,
        } = this.state;
        const normalizedSelectedIds = this.normalizeIds(selectedIds || []);

        return userId && countryId ? (
            <Fragment>
                <Button
                    onClick={this.handleClick}
                    className={classes.actionButton}
                    {...sanitizeRestProps(rest)}
                >
                    {icon}
                    {translate(label, { _: label })}
                </Button>
                <Dialog open={isOpen} onClose={this.handleDialogClose}>
                    <DialogTitle>Create Request</DialogTitle>
                    <DialogContent>
                        <Form
                            onSubmit={this.createRequest}
                            render={({
                                handleSubmit,
                                form,
                                submitting,
                                pristine,
                                values,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <ReferenceInput
                                        label="Partner"
                                        source="partnerId"
                                        reference="partners"
                                        resource="partners"
                                        className={classes.input}
                                        fullWidth
                                    >
                                        <SelectInput optionText="name" />
                                    </ReferenceInput>
                                    <ReferenceInput
                                        label="Order"
                                        source="orderId"
                                        reference="orders"
                                        resource="orders"
                                        filter={{
                                            userId,
                                            status: [
                                                OrderStatus.confirmed,
                                                OrderStatus.complete,
                                            ],
                                        }}
                                        className={classes.input}
                                        fullWidth
                                    >
                                        <SelectInput
                                            source="orderId"
                                            optionValue="id"
                                            optionText={record =>
                                                `Order #${record.id}`
                                            }
                                        />
                                    </ReferenceInput>
                                    <SelectInput
                                        source="type"
                                        className={classes.input}
                                        fullWidth
                                        choices={Object.keys(RequestType).map(
                                            c => ({
                                                id: c,
                                                name: c,
                                            })
                                        )}
                                    />
                                    <DateInput
                                        source="date"
                                        className={classes.input}
                                        parse={v =>
                                            v && moment(v).format('YYYY-MM-DD')
                                        }
                                        fullWidth
                                    />
                                    <SelectInput
                                        source="from"
                                        className={classes.input}
                                        translateChoice={false}
                                        choices={PickupTimeFrom.map(c => ({
                                            id: c,
                                            name: c,
                                        }))}
                                        fullWidth
                                    />
                                    <SelectInput
                                        source="to"
                                        className={classes.input}
                                        translateChoice={false}
                                        choices={PickupTimeTo.map(c => ({
                                            id: c,
                                            name: c,
                                        }))}
                                        fullWidth
                                    />
                                    <ReferenceInput
                                        label="City"
                                        source="cityId"
                                        reference="city"
                                        resource="city"
                                        className={classes.input}
                                        fullWidth
                                    >
                                        <SelectInput optionText="name" />
                                    </ReferenceInput>
                                    <div style={{ marginTop: '0.5rem' }}>
                                        <Typography variant="body2">
                                            Selected in list:{' '}
                                            {normalizedSelectedIds.length}
                                        </Typography>
                                        {previewLoading ? (
                                            <Typography variant="body2">
                                                Calculating parent/parts
                                                inclusion...
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2">
                                                Will be included in request:{' '}
                                                {expandedSelectedIds.length}
                                                {autoAddedItems.length > 0
                                                    ? ` (auto-added ${autoAddedItems.length} parent/part items)`
                                                    : ''}
                                            </Typography>
                                        )}
                                        {!previewLoading &&
                                            previewItems.length > 0 && (
                                                <div
                                                    style={{
                                                        marginTop: '0.5rem',
                                                        border:
                                                            '1px solid #e0e0e0',
                                                        borderRadius: 4,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <table
                                                        style={{
                                                            width: '100%',
                                                            borderCollapse:
                                                                'collapse',
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        <thead>
                                                            <tr
                                                                style={{
                                                                    background:
                                                                        '#f5f5f5',
                                                                }}
                                                            >
                                                                <th
                                                                    style={{
                                                                        textAlign:
                                                                            'left',
                                                                        padding:
                                                                            '6px 8px',
                                                                    }}
                                                                >
                                                                    ID
                                                                </th>
                                                                <th
                                                                    style={{
                                                                        textAlign:
                                                                            'left',
                                                                        padding:
                                                                            '6px 8px',
                                                                    }}
                                                                >
                                                                    Code
                                                                </th>
                                                                <th
                                                                    style={{
                                                                        textAlign:
                                                                            'left',
                                                                        padding:
                                                                            '6px 8px',
                                                                    }}
                                                                >
                                                                    Type
                                                                </th>
                                                                <th
                                                                    style={{
                                                                        textAlign:
                                                                            'left',
                                                                        padding:
                                                                            '6px 8px',
                                                                    }}
                                                                >
                                                                    Reason
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {previewItems.map(
                                                                (item: any) => (
                                                                    <tr
                                                                        key={
                                                                            item.id
                                                                        }
                                                                    >
                                                                        <td
                                                                            style={{
                                                                                padding:
                                                                                    '6px 8px',
                                                                                borderTop:
                                                                                    '1px solid #eee',
                                                                            }}
                                                                        >
                                                                            {
                                                                                item.id
                                                                            }
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                padding:
                                                                                    '6px 8px',
                                                                                borderTop:
                                                                                    '1px solid #eee',
                                                                            }}
                                                                        >
                                                                            {
                                                                                item.code
                                                                            }
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                padding:
                                                                                    '6px 8px',
                                                                                borderTop:
                                                                                    '1px solid #eee',
                                                                            }}
                                                                        >
                                                                            {item.isPart
                                                                                ? 'Part'
                                                                                : 'Parent'}
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                padding:
                                                                                    '6px 8px',
                                                                                borderTop:
                                                                                    '1px solid #eee',
                                                                            }}
                                                                        >
                                                                            {
                                                                                item.reason
                                                                            }
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        {previewError && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                            >
                                                {previewError}
                                            </Typography>
                                        )}
                                    </div>

                                    <DialogActions>
                                        <Button
                                            disabled={loading}
                                            onClick={this.handleDialogClose}
                                        >
                                            <AlertError
                                                className={
                                                    classes.iconPaddingStyle
                                                }
                                            />
                                            {translate(cancel, { _: cancel })}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            disabled={
                                                submitting || previewLoading
                                            }
                                            className={classnames(
                                                'ra-confirm',
                                                {
                                                    [classes.confirmWarning]:
                                                        confirmColor ===
                                                        'warning',
                                                    [classes.confirmPrimary]:
                                                        confirmColor ===
                                                        'primary',
                                                }
                                            )}
                                            autoFocus
                                        >
                                            <ActionCheck
                                                className={
                                                    classes.iconPaddingStyle
                                                }
                                            />
                                            {translate(confirm, { _: confirm })}
                                        </Button>
                                    </DialogActions>
                                </form>
                            )}
                        />
                    </DialogContent>
                </Dialog>
            </Fragment>
        ) : null;
    }
}

const mapState = (state, props) => {
    const storages = Object.values(
        _.pick(state.admin.resources.storages.data, props.selectedIds)
    );
    const userId =
        storages &&
        storages.length &&
        _.every(storages, ['userId', storages[0].userId])
            ? storages[0].userId
            : null;
    const countryId =
        storages &&
        storages.length &&
        _.every(storages, ['countryId', storages[0].countryId])
            ? storages[0].countryId
            : null;

    return {
        countryId,
        userId,
    };
};

const mapDispatch = {
    crudCreate,
};

const connector = connect(mapState, mapDispatch);

const ConnectedCreateRequest = connector(CreateRequest);

export default props => {
    const classes = useStyles();
    const translate = useTranslate();
    return (
        <ConnectedCreateRequest
            classes={classes}
            translate={translate}
            {...props}
        />
    );
};
